"use client";

import { useEffect, useMemo, useRef, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faAngleDown } from "@fortawesome/pro-regular-svg-icons/faAngleDown";
import {
  Input,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  useToast,
  cn,
} from "@wondermaps/ui";
import { type POI } from "../MapCanvas";
import { type CategoryForCarousel, CategoryCarousel } from "./CategoryCarousel";
import { POIListItem } from "./POIListItem";
import { VisitorPageHeader } from "./VisitorPageHeader";
import { type SortMode } from "../../hooks/useSortPreference";
import { analytics, useSearchTracking } from "../../hooks/useAnalytics";
import type { VisitorNotification } from "./notification-types";

interface SearchPageProps {
  mapSlug: string;
  logoUrl?: string;
  appName?: string;
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  shareUrl: string;
  shareTitle: string;
  shareText?: string;
  pois: POI[];
  categories: CategoryForCarousel[];
  selectedCategory: string;
  onSelectCategory: (slug: string) => void;
  onSelectPOI: (poi: POI) => void;
  poiCounts: Record<string, number>;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchInputRef?: Ref<HTMLInputElement>;
  userLocation: { lng: number; lat: number } | null;
  distances: Map<string, number>;
  sortMode: SortMode;
  onSortChange: (mode: SortMode) => void;
  savedCount: number;
  isBookmarked?: (poiId: string) => boolean;
  onBookmarkToggle?: (poiId: string) => void;
  bookmarksEnabled: boolean;
  happeningNowCount: number;
  accessibleMode?: boolean;
  brandAccent?: string | null;
  onRequestLocation: () => void;
  curatedListInfo?: { name: string; description: string | null } | null;
}

const ITEM_HEIGHT = 80;
const SECTION_HEADER_HEIGHT = 36;
const OVERSCAN = 6;

type VirtualListItem =
  | { type: "poi"; poi: POI }
  | { type: "header"; category: string };

// Pure sort helpers — module-scope so identity is stable across renders.
function getSponsorPriority(poi: POI): number {
  if (poi.sponsorRef) return 1000 - poi.sponsorRef.sponsorTierSortOrder;
  if (poi.sponsorTier === "gold") return 1;
  if (poi.sponsorTier === "silver") return 2;
  if (poi.sponsorTier === "bronze") return 3;
  return 9999;
}

function getStatusPriority(status: POI["status"]): number {
  return status === "temp_closed" ? 1 : 0;
}

export function SearchPage({
  mapSlug,
  logoUrl,
  appName,
  notifications,
  onBellClick,
  isTrayOpen,
  shareUrl,
  shareTitle,
  shareText,
  pois,
  categories,
  selectedCategory,
  onSelectCategory,
  onSelectPOI,
  poiCounts,
  searchQuery,
  onSearchChange,
  searchInputRef,
  userLocation,
  distances,
  sortMode,
  onSortChange,
  savedCount,
  isBookmarked,
  onBookmarkToggle,
  bookmarksEnabled,
  happeningNowCount,
  accessibleMode,
  brandAccent,
  onRequestLocation,
  curatedListInfo,
}: SearchPageProps) {
  const { t, i18n } = useTranslation("map");
  const { t: tPois } = useTranslation("pois");
  const { toast } = useToast();
  const { trackSearch, trackSearchConversion } = useSearchTracking();
  const lang = i18n.language;
  const parentRef = useRef<HTMLDivElement>(null);

  // Build placeholder: first 3 admin-sorted categories that are visible,
  // filterable, AND have at least 1 POI. Mirrors CategoryCarousel's visibility rule.
  const placeholder = useMemo(() => {
    const top = categories
      .filter(
        (c) =>
          (poiCounts[c.slug] ?? 0) > 0 &&
          c.isVisible !== false &&
          c.isFilterable !== false,
      )
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .slice(0, 3)
      .map((c) => {
        const dbName = lang !== "en" ? c.nameTranslations?.[lang] : undefined;
        const name =
          dbName ?? tPois(`category.${c.slug}`, { defaultValue: c.name });
        return name.toLowerCase();
      });
    if (top.length === 0) return t("search.placeholder");
    return t("search.placeholderTemplate", { categories: top.join(", ") });
  }, [categories, poiCounts, lang, t, tPois]);

  // Track search analytics
  useEffect(() => {
    if (searchQuery) trackSearch(searchQuery, pois.length);
  }, [searchQuery, pois.length, trackSearch]);

  // Sort change w/ GPS gating
  const handleSortChange = (mode: SortMode) => {
    if (mode === "distance" && !userLocation) {
      if (!navigator.geolocation) {
        toast({
          title: t("list.locationUnavailable"),
          description: t("list.noGeolocation"),
          variant: "destructive",
        });
        return;
      }
      onRequestLocation();
      navigator.geolocation.getCurrentPosition(
        () => {
          onSortChange("distance");
          analytics.interactionGps({ trigger: "sort_by_distance" });
          analytics.nearbySort({ mode: "distance" });
        },
        () => {
          toast({ title: t("list.enableLocation"), variant: "destructive" });
        },
        { timeout: 10000, maximumAge: 300000 },
      );
      return;
    }
    onSortChange(mode);
    analytics.nearbySort({ mode });
  };

  const categoryNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cat of categories) {
      const dbTranslation =
        lang !== "en" ? cat.nameTranslations?.[lang] : undefined;
      map[cat.slug] =
        dbTranslation ?? tPois(`category.${cat.slug}`, { defaultValue: cat.name });
    }
    return map;
  }, [categories, lang, tPois]);

  const virtualItems = useMemo((): VirtualListItem[] => {
    if (sortMode === "category") {
      const grouped: Record<string, POI[]> = {};
      for (const poi of pois) {
        const cat = poi.categorySlug || "uncategorized";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(poi);
      }
      const order = categories.reduce<Record<string, number>>((acc, c, i) => {
        acc[c.slug] = c.sortOrder ?? i;
        return acc;
      }, {});
      const sortedCats = Object.keys(grouped).sort(
        (a, b) => (order[a] ?? 999) - (order[b] ?? 999),
      );
      const items: VirtualListItem[] = [];
      for (const cat of sortedCats) {
        const catPois = grouped[cat].sort((a, b) => {
          const s = getSponsorPriority(a) - getSponsorPriority(b);
          if (s !== 0) return s;
          const st = getStatusPriority(a.status) - getStatusPriority(b.status);
          if (st !== 0) return st;
          return a.name.localeCompare(b.name);
        });
        items.push({ type: "header", category: categoryNameMap[cat] || cat });
        for (const poi of catPois) items.push({ type: "poi", poi });
      }
      return items;
    }

    const sorted = [...pois].sort((a, b) => {
      const s = getSponsorPriority(a) - getSponsorPriority(b);
      if (s !== 0) return s;
      const st = getStatusPriority(a.status) - getStatusPriority(b.status);
      if (st !== 0) return st;
      if (sortMode === "distance") {
        const da = distances.get(a.id) ?? Infinity;
        const db = distances.get(b.id) ?? Infinity;
        return da - db;
      }
      return a.name.localeCompare(b.name);
    });
    return sorted.map((poi) => ({ type: "poi" as const, poi }));
  }, [pois, sortMode, distances, categories, categoryNameMap]);

  const poiCount = virtualItems.filter((i) => i.type === "poi").length;

  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      virtualItems[index]?.type === "header"
        ? SECTION_HEADER_HEIGHT
        : ITEM_HEIGHT,
    overscan: OVERSCAN,
  });

  useEffect(() => {
    if (pois.length > 0) {
      const timer = setTimeout(() => virtualizer.measure(), 0);
      return () => clearTimeout(timer);
    }
  }, [pois.length, virtualizer]);

  useEffect(() => {
    if (parentRef.current) parentRef.current.scrollTop = 0;
  }, [selectedCategory]);

  const selectedCategoryName =
    selectedCategory !== "all" && selectedCategory !== "saved"
      ? categoryNameMap[selectedCategory]
      : undefined;

  const sortLabel =
    sortMode === "distance"
      ? t("list.sortDistance")
      : sortMode === "category"
        ? t("list.sortCategory")
        : t("list.sortDefault");

  return (
    <div
      className="absolute inset-0 z-10 bg-background flex flex-col"
      style={{ paddingBottom: "var(--bottom-nav-height, 0px)" }}
    >
      <VisitorPageHeader
        logoUrl={logoUrl}
        appName={appName}
        showShare
        shareUrl={shareUrl}
        shareTitle={shareTitle}
        shareText={shareText}
        brandAccent={brandAccent}
        notifications={notifications}
        onBellClick={onBellClick}
        isTrayOpen={isTrayOpen}
        mapSlug={mapSlug}
      />

      {/* Search input */}
      <div className="px-4 pt-2 pb-3 shrink-0">
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
            aria-hidden
          />
          <Input
            ref={searchInputRef}
            type="text"
            inputMode="search"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={cn(
              "h-10 pl-11 rounded-full shadow-xs text-ellipsis",
              searchQuery ? "pr-12" : "pr-4",
            )}
            aria-label={t("search.ariaLabel")}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full hover:bg-muted"
              aria-label={t("search.clear")}
            >
              <FontAwesomeIcon
                icon={faXmark}
                className="w-4 h-4 text-muted-foreground"
              />
            </button>
          )}
        </div>
      </div>

      {/* Category pills */}
      <CategoryCarousel
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={onSelectCategory}
        poiCounts={poiCounts}
        savedCount={savedCount}
        happeningNowCount={happeningNowCount}
        className="!bg-transparent !shadow-none border-0 !py-0 !px-4"
        brandAccent={brandAccent}
      />

      {/* Sort row — muted text + caret dropdown */}
      <div className="flex justify-end px-4 pt-3 pb-2 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t("search.sortBy", { defaultValue: "Sort by" })}
            >
              <span className="text-foreground/40">
                {t("search.sortBy", { defaultValue: "Sort by" })}{" "}
              </span>
              <span className="font-medium text-foreground">{sortLabel}</span>
              <FontAwesomeIcon
                icon={faAngleDown}
                className="w-3 h-3 text-foreground"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => handleSortChange("default")}>
              {t("list.sortDefault")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handleSortChange("distance")}>
              {t("list.sortDistance")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handleSortChange("category")}>
              {t("list.sortCategory")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* List */}
      <div ref={parentRef} className="flex-1 overflow-y-auto">
        {curatedListInfo && selectedCategory === "saved" && (
          <div className="px-4 py-3 border-b bg-muted/30">
            <p className="font-semibold text-sm">{curatedListInfo.name}</p>
            {curatedListInfo.description && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {curatedListInfo.description}
              </p>
            )}
          </div>
        )}
        {poiCount === 0 ? (
          <div className="flex h-full items-center justify-center px-4 pb-20">
            <p className="text-center text-muted-foreground whitespace-pre-line">
              {searchQuery
                ? t("list.emptySearch", { query: searchQuery })
                : selectedCategory === "saved"
                  ? t("list.emptySaved")
                  : selectedCategoryName
                    ? t("list.emptyCategory", { category: selectedCategoryName })
                    : t("list.emptyDefault")}
            </p>
          </div>
        ) : (
          <div
            role="list"
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((vItem) => {
              const item = virtualItems[vItem.index];
              if (item.type === "header") {
                return (
                  <div
                    key={`header-${item.category}-${vItem.index}`}
                    className="sticky top-0 z-10 bg-muted px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${vItem.size}px`,
                      transform: `translateY(${vItem.start}px)`,
                    }}
                  >
                    {item.category}
                  </div>
                );
              }
              const poi = item.poi;
              return (
                <div
                  key={poi.id}
                  role="listitem"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${vItem.size}px`,
                    transform: `translateY(${vItem.start}px)`,
                  }}
                >
                  <POIListItem
                    poi={poi}
                    distance={userLocation ? distances.get(poi.id) : undefined}
                    onClick={() => {
                      if (searchQuery && searchQuery.length >= 2) {
                        trackSearchConversion(searchQuery, {
                          id: poi.id,
                          name: poi.name,
                        });
                      }
                      onSelectPOI(poi);
                    }}
                    isBookmarked={
                      bookmarksEnabled ? isBookmarked?.(poi.id) : undefined
                    }
                    onBookmarkToggle={
                      bookmarksEnabled ? onBookmarkToggle : undefined
                    }
                    accessibleMode={accessibleMode}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
